import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInAnonymously,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
} from 'firebase/auth';
import { ref, set, get } from 'firebase/database';
import { auth, db } from '../config/firebase';
import { UserProfile } from '../types/user';

export async function validateBetaCode(code: string): Promise<boolean> {
  const snapshot = await get(ref(db, `betaCodes/${code}`));
  if (!snapshot.exists()) return false;
  const data = snapshot.val();
  return data.active === true;
}

export async function incrementBetaCodeUsage(code: string): Promise<void> {
  const countRef = ref(db, `betaCodes/${code}/usedCount`);
  const current = (await get(countRef)).val() || 0;
  await set(countRef, current + 1);
}

export async function signUp(
  email: string,
  password: string,
  displayName: string,
  betaCode: string
): Promise<string> {
  const isValid = await validateBetaCode(betaCode);
  if (!isValid) throw new Error('Invalid beta code');

  const credential = await createUserWithEmailAndPassword(auth, email, password);
  const uid = credential.user.uid;

  const profile: UserProfile = {
    uid,
    displayName,
    email,
    createdAt: Date.now(),
    tripIds: [],
  };

  await set(ref(db, `users/${uid}`), profile);
  await updateProfile(credential.user, { displayName });
  await incrementBetaCodeUsage(betaCode);

  if (credential.user) {
    await sendEmailVerification(credential.user);
  }

  return uid;
}

export async function logIn(email: string, password: string): Promise<string> {
  const credential = await signInWithEmailAndPassword(auth, email, password);
  return credential.user.uid;
}

export async function logOut(): Promise<void> {
  await signOut(auth);
}

export async function signInAnon(): Promise<string> {
  const credential = await signInAnonymously(auth);
  return credential.user.uid;
}

export async function resetPassword(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email);
}
